// TODO(stripe-reimpl): this file has legacy Stripe billing code, disabled while we're betaAccess-only. Rebuild on the new billing system.
import React, { useState } from "react";
import { Alert, Button } from "@mui/material";
import { SettingsRow, SettingsSection } from "../SettingsSection";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import capitalize from "../../../utils/helpers/capitalize";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import { useAuth } from "hooks/useAuth";
import { format, fromUnixTime } from "date-fns";
import { useTranslation } from "react-i18next";
import { dateLocale } from "i18n";

const PlanManagement = ({ subscriptionTier, loading }) => {
  const [actionLoading, setActionLoading] = useState(null);
  const { userData, subscriptionData } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation("account");

  const handlePortalAction = async (flowType) => {
    setActionLoading(flowType);
    try {
      const createPortalSession = firebase
        .functions()
        .httpsCallable("createPortalSession");

      const { data } = await createPortalSession({
        successUrl: window.location.href,
        cancelUrl: window.location.href,
        role: userData?.role,
        stripeId: userData?.stripeId,
        flowType,
        subscriptionId: subscriptionData?.id,
      });

      if (data.url) {
        window.location.assign(data.url);
      }
    } catch (error) {
      console.error("Error opening portal:", error);
      alert(t("plan.portalFailed"));
    }
  };

  const handleManageBilling = async () => {
    setActionLoading("manage_billing");
    try {
      const createPortalSession = firebase
        .functions()
        .httpsCallable("createPortalSession");

      const { data } = await createPortalSession({
        successUrl: window.location.href,
        cancelUrl: window.location.href,
        role: userData?.role,
        stripeId: userData?.stripeId,
        // No flowType = regular portal with billing history, payment methods, invoices
      });

      if (data.url) {
        window.location.assign(data.url);
      }
    } catch (error) {
      console.error("Error opening portal:", error);
      alert(t("plan.billingFailed"));
    }
  };

  const handleChangePlan = () => {
    // Navigate to your upgrade page instead of portal
    navigate("/dashboard/upgrade");
  };

  const formatDate = (timestamp) => {
    // Handle Firebase timestamp objects
    return format(timestamp.toDate(), t("plan.dateFormat"), {
      locale: dateLocale(),
    });
  };

  // Get appropriate plan name and button text based on user role
  const getPlanName = () => {
    if (userData?.role === "facility") {
      // Only show "CareMap Plus" if they actually have subscription data
      return subscriptionData ? "CareMap Plus" : t("plan.unknownPlan");
    }
    const tier = subscriptionTier || "medium";
    return t("plan.planName", {
      tier: t(`tiers.${tier}`, { defaultValue: capitalize(tier) }),
    });
  };

  const getChangePlanButtonText = () => {
    if (userData?.role === "facility") {
      return t("plan.changeBillingCycle");
    }
    return t("common.change");
  };

  const cancelling = subscriptionData?.cancel_at_period_end;

  return (
    <SettingsSection title={t("plan.title")}>
      {cancelling && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {t("plan.cancelScheduled", {
            date: formatDate(subscriptionData.current_period_end),
          })}
        </Alert>
      )}
      <SettingsRow
        label={t("plan.currentPlan")}
        detail={getPlanName()}
        action={
          !cancelling && (
            <Button variant="outlined" size="small" onClick={handleChangePlan}>
              {getChangePlanButtonText()}
            </Button>
          )
        }
      />
      <SettingsRow
        label={t("plan.billing")}
        detail={t("plan.billingDetail")}
        action={
          <LoadingButton
            variant="outlined"
            size="small"
            loading={actionLoading === "manage_billing"}
            disabled={!!actionLoading}
            onClick={handleManageBilling}
          >
            {cancelling ? t("plan.renew") : t("plan.manage")}
          </LoadingButton>
        }
      />
      {!cancelling && (
        <SettingsRow
          label={t("plan.subscription")}
          detail={t("plan.cancelDetail")}
          action={
            <LoadingButton
              variant="outlined"
              size="small"
              color="error"
              loading={actionLoading === "subscription_cancel"}
              disabled={!!actionLoading}
              onClick={() => handlePortalAction("subscription_cancel")}
            >
              {t("common.cancel")}
            </LoadingButton>
          }
        />
      )}
    </SettingsSection>
  );
};

export default PlanManagement;
